import { type FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';

import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import cx from 'classnames';

import CopyIcon from '~/components/Icons/CopyIcon';
import FacebookIcon from '~/components/Icons/FacebookIcon';
import LinkedInIcon from '~/components/Icons/LinkedInIcon';
import ShareIcon from '~/components/Icons/ShareIcon';
import TwitterIcon from '~/components/Icons/TwitterIcon';
import WhatsAppIcon from '~/components/Icons/WhatsAppIcon';
import useCopyToClipboard from '~/hooks/useCopyToClipboard';
import useIsMobile from '~/hooks/useIsMobile';

import { BottomSheetDialog } from '../../ui/BottomSheetDialog';

import { actionIconClass } from '../constants';

interface ShareMenuProps {
  url: string;
  title: string;
}

const SHARE_ITEMS = [
  {
    key: 'x',
    labelKey: 'community.share.share_to_x',
    fallback: 'Share to X',
    Icon: TwitterIcon,
    Button: TwitterShareButton,
  },
  {
    key: 'facebook',
    labelKey: 'community.share.share_to_facebook',
    fallback: 'Share to Facebook',
    Icon: FacebookIcon,
    Button: FacebookShareButton,
  },
  {
    key: 'whatsapp',
    labelKey: 'community.share.share_to_whatsapp',
    fallback: 'Share to WhatsApp',
    Icon: WhatsAppIcon,
    Button: WhatsappShareButton,
  },
  {
    key: 'linkedin',
    labelKey: 'community.share.share_to_linkedin',
    fallback: 'Share to LinkedIn',
    Icon: LinkedInIcon,
    Button: LinkedinShareButton,
  },
] as const;

const mobileItemBase =
  'flex items-center w-full gap-4 h-16 px-5 text-base font-normal leading-6 text-black whitespace-nowrap';
const desktopItemBase =
  'flex w-full items-center gap-2 h-10 px-4 text-left text-sm font-medium leading-[14px] tracking-[-0.14px] whitespace-nowrap text-black';
const iconClass = 'flex items-center justify-center size-4 shrink-0';
const triggerClass = 'flex items-center justify-center size-8 rounded-full hover:bg-black/5 transition-colors';

export const ShareMenu: FC<ShareMenuProps> = ({ url, title }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const copyToClipboard = useCopyToClipboard();

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const handleCopyLink = () => {
    copyToClipboard(url);
  };

  if (isMobile) {
    return (
      <>
        <button type="button" className={triggerClass} aria-label="Share" onClick={() => setIsOpen(true)}>
          <ShareIcon className={actionIconClass} fill="none" />
        </button>

        <BottomSheetDialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <div className="relative flex items-center justify-center px-5 pt-5 pb-2">
            <Dialog.Title className="text-base font-semibold text-black">
              {t('community.share.share_post', 'Share post')}
            </Dialog.Title>
            <button
              type="button"
              className="absolute right-5 flex items-center justify-center size-8 rounded-full hover:bg-black/5"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              <XMarkIcon className="size-5 text-black/50" />
            </button>
          </div>

          <div className="flex flex-col pb-2">
            <button
              type="button"
              className={cx(mobileItemBase, 'border-b border-black/10 hover:bg-black/5 transition-colors')}
              onClick={() => {
                handleCopyLink();
                setIsOpen(false);
              }}
            >
              <span className={iconClass}>
                <CopyIcon className="size-4" />
              </span>
              <span>{t('community.share.copy_link', 'Copy link')}</span>
            </button>

            {SHARE_ITEMS.map(({ key, labelKey, fallback, Icon, Button }) => (
              <Button key={key} url={url} title={title} className="!w-full" onClick={() => setIsOpen(false)}>
                <div className={cx(mobileItemBase, 'hover:bg-black/5 transition-colors')}>
                  <span className={iconClass}>
                    <Icon className="size-4" />
                  </span>
                  <span>{t(labelKey, fallback)}</span>
                </div>
              </Button>
            ))}
          </div>
        </BottomSheetDialog>
      </>
    );
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button ref={refs.setReference} className={triggerClass} aria-label="Share">
        <ShareIcon className={actionIconClass} fill="none" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition-opacity ease-out duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Menu.Items
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-50 min-w-0 overflow-hidden rounded-3xl border border-black/5 bg-white py-1 shadow-[0_4px_6px_rgba(0,0,0,0.08)] focus:outline-none"
        >
          <div className="px-4 pb-1 pt-2 text-[11px] leading-none text-black/50" aria-hidden="true">
            {t('community.share.share_post', 'Share post')}
          </div>

          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                className={cx(desktopItemBase, 'transition-colors', { 'bg-black/5': active })}
                onClick={handleCopyLink}
              >
                <span className={iconClass}>
                  <CopyIcon className="size-4" />
                </span>
                <span>{t('community.share.copy_link', 'Copy link')}</span>
              </button>
            )}
          </Menu.Item>

          {SHARE_ITEMS.map(({ key, labelKey, fallback, Icon, Button }) => (
            <Menu.Item key={key}>
              {({ active }) => (
                <Button url={url} title={title} className="!w-full">
                  <div
                    className={cx(desktopItemBase, 'transition-colors', {
                      'bg-black/5': active,
                    })}
                  >
                    <span className={iconClass}>
                      <Icon className="size-4" />
                    </span>
                    <span>{t(labelKey, fallback)}</span>
                  </div>
                </Button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
